import { Modal } from '../../../components/modal/modal';
import React, { useContext, useEffect, useState } from 'react';
import { Spinner } from '../../../components/spinner/spinner';
import { Field, FieldLabel } from '../../../components/field/field';
import { toast } from '../../../util/toasts';
import { CreatableSelect } from '../../../components/select-form/select-form';
import { Phone } from '../../../components/field/phone';
import { useRepresentativeApi } from '../../../clients/quipli-service/representatives/representative-api';
import {
  Representative,
  UpsertRepresentativeRequest,
  RepresentativeRole,
} from '../../../clients/quipli-service/representatives/representative';
import { useNavigate } from 'react-router-dom';
import { TenantContext } from '../../../context/tenant-context';

export const UserRepresentativeForm = ({
  isOpen,
  userId,
  representative,
  setRepresentative,
  onClose,
  onSubmit,
}: {
  isOpen: boolean;
  userId: number;
  representative?: Representative;
  setRepresentative?: (r: Representative) => void;
  onClose: () => void;
  onSubmit?: () => void;
}) => {
  const { contextPath } = useContext(TenantContext);
  const { upsertRepresentative } = useRepresentativeApi();
  const { searchRoles } = useRepresentativeApi();
  const navigate = useNavigate();

  // React states
  const [isLoading, setLoading] = useState<boolean>(false);
  const [isRequestInProgress, setRequestInProgress] = useState<boolean>(false);
  const [representativeRequest, setRepresentativeRequest] = useState<UpsertRepresentativeRequest>(
    representative ?? { userId, name: '', email: '', phone: '' },
  );
  const [phone, setPhone] = useState<string | undefined>(representative?.phone);
  const [phoneIsValid, setPhoneIsValid] = useState<boolean>(true);
  const [roleOptions, setRoleOptions] = useState<RepresentativeRole[]>([]);
  const [role, setRole] = useState<RepresentativeRole | undefined>(representative?.role);

  const submitRepresentative = async () => {
      if (phone !== '' && !phoneIsValid) {
        toast('Please enter a valid phone number', 'error');
        return;
      }

      setRequestInProgress(true);

      try {
        const body = representativeRequest;
        const result = await upsertRepresentative({
          ...body,
          phone: phone,
          email: body.email && body.email.length > 0 ? body.email : 'customer@quipli.com',
          roleId: role?.id,
        });
        setRepresentative && setRepresentative(result);
        onSubmit?.();
      } catch (e: any) {
        toast(e.message.msg ?? e.message ?? 'Failed to save representative', 'error');
      } finally {
        setRequestInProgress(false);
      }
    },
    isBtnDisabled = (): boolean => {
      return isRequestInProgress;
    },
    closeModal = () => {
      // Don't close modal if there is a request in progress
      if (isRequestInProgress) return;

      onClose();
    };

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const { items } = await searchRoles({ includeTemplates: false });
      setRoleOptions(items);
    } catch (e: any) {
      toast(e.message.msg ?? e.message ?? 'Failed to fetch roles', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (representative) {
      setRepresentativeRequest(representative);
    }

    fetchRoles();
  }, []);

  return (
    <>
      <Modal
        title="Company Representative"
        isOpen={isOpen}
        close={closeModal}
        actions={
          isRequestInProgress ? (
            <Spinner />
          ) : (
            <>
              <button
                className="button button-small button-block button-secondary"
                disabled={isRequestInProgress}
                onClick={() => closeModal()}
              >
                Cancel
              </button>

              <button
                className="button button-small button-block button-yellow"
                disabled={isBtnDisabled()}
                onClick={() => submitRepresentative()}
              >
                Save
              </button>
            </>
          )
        }
      >
        <Field
          type="text"
          label="Full name"
          dataCy="full_name"
          value={representativeRequest.name}
          onChange={(name: string) => {
            setRepresentativeRequest((prev) => ({ ...prev, name }));
          }}
          isRequired={true}
        />

        <Phone label="Phone number" value={phone} onChange={setPhone} onChangeValidity={setPhoneIsValid} />

        <Field
          type="text"
          label="Title"
          dataCy="representative_title"
          value={representativeRequest.title}
          onChange={(title: string) => {
            setRepresentativeRequest((prev) => ({ ...prev, title }));
          }}
          isRequired={false}
        />

        <div>
          <FieldLabel label="Role" isRequired={false} />
          <CreatableSelect
            className="h-fit text-[14px]"
            value={role}
            onChange={(role) => setRole(role || undefined)}
            options={roleOptions}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.id}
            isRequired={false}
            isClearable
            menuPortalTarget={document.body}
            isLoading={isLoading}
            creatableOptionText="Create new role"
            onClickCreatableOption={() =>
              navigate(`${contextPath}manager/representatives/roles/create`, {
                state: { redirectToBack: true },
              })
            }
          />
        </div>

        <Field
          type="text"
          label="Email"
          value={representativeRequest.email}
          onChange={(email: string) => {
            setRepresentativeRequest((prev) => ({ ...prev, email: email.length > 0 ? email : undefined }));
          }}
        />
      </Modal>
    </>
  );
};
